import { useState } from 'react';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';

import { BoardTemplate, SpaceType } from '../../mainApi';
import { getTemplateDescriptionText } from './templateDescription';

type TemplateTypeMeta = {
  label: string;
  image: string;
};

const templateTypeMeta: Record<SpaceType, TemplateTypeMeta> = {
  board: {
    label: 'Board',
    image: 'https://storage.googleapis.com/dotstorming-img/graphic_voting.svg',
  },
  wall: {
    label: 'Whiteboard',
    image: 'https://storage.googleapis.com/dotstorming-img/graphic_wall.svg',
  },
  collage: {
    label: 'Collage ',
    image: 'https://storage.googleapis.com/dotstorming-img/graphic_collage.svg',
  },
  game: {
    label: 'Quick Sort',
    image: 'https://storage.googleapis.com/dotstorming-img/graphic_game.svg',
  },
};

const getTemplateTypeMeta = (template: BoardTemplate) =>
  templateTypeMeta[template.spaceType ?? 'board'] ?? templateTypeMeta.board;

type TemplateCardProps = {
  template: BoardTemplate;
  isDeleting?: boolean;
  isCreating?: boolean;
  isCopying?: boolean;
  isSharing?: boolean;
  isUpdating?: boolean;
  onCreate?: (template: BoardTemplate) => void;
  onCopy?: (template: BoardTemplate) => void;
  onEdit?: (template: BoardTemplate) => void;
  onShare?: (template: BoardTemplate) => void;
  onDelete?: (template: BoardTemplate) => void;
};

export const TemplateCard = ({
  template,
  isDeleting = false,
  isCreating = false,
  isCopying = false,
  isSharing = false,
  isUpdating = false,
  onCreate,
  onCopy,
  onEdit,
  onShare,
  onDelete,
}: TemplateCardProps) => {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const typeMeta = getTemplateTypeMeta(template);
  const descriptionText = getTemplateDescriptionText(template.description);
  const categoryChips = (template.categories ?? []).filter(Boolean);
  const isMenuOpen = Boolean(menuAnchor);
  const isBusy = isDeleting || isCreating || isCopying || isSharing || isUpdating;

  return (
    <Card
      sx={{
        position: 'relative',
        border: '1px solid #e6e8ef',
        borderRadius: '6px',
        boxShadow: 'none',
        bgcolor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          borderColor: '#d8dde7',
          boxShadow: '0 2px 8px rgba(2, 43, 58, 0.08)',
        },
      }}
    >
      <CardHeader
        avatar={
          <Box
            component="img"
            src={typeMeta.image}
            alt=""
            sx={{
              width: 28,
              height: 28,
              objectFit: 'contain',
            }}
          />
        }
        action={
          onEdit || onShare || onDelete ? (
            <IconButton
              size="small"
              disabled={isBusy}
              aria-label={`Template actions for ${template.name || 'Untitled template'}`}
              sx={{
                color: '#022b3a',
              }}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setMenuAnchor(event.currentTarget);
              }}
            >
              <MoreVertOutlinedIcon fontSize="small" />
            </IconButton>
          ) : null
        }
        title={template.name || 'Untitled template'}
        sx={{
          alignItems: 'center',
          borderBottom: '1px solid #eef0f4',
          '.MuiCardHeader-content': {
            minWidth: 0,
          },
          '.MuiCardHeader-action': {
            alignSelf: 'center',
            mr: 0,
          },
          '.MuiCardHeader-title': {
            fontSize: 18,
            lineHeight: 1.25,
            fontWeight: 700,
            color: '#022b3a',
            overflowWrap: 'anywhere',
          },
        }}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          '&:last-child': { pb: 2.5 },
        }}
      >
        <Typography sx={{ whiteSpace: 'pre-line' }}>
          {descriptionText || 'No description added.'}
        </Typography>
      </CardContent>
      {categoryChips.length > 0 ? (
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          sx={{
            mt: 'auto',
            px: 2,
            pt: 1,
            pb: 1.5,
          }}
        >
          {categoryChips.map((category) => (
            <Chip
              key={category}
              label={category}
              size="small"
              sx={{
                height: 24,
                fontSize: 12,
                fontWeight: 700,
                bgcolor: '#e8f4ff',
                color: '#0f4c81',
                borderRadius: 1,
              }}
            />
          ))}
        </Stack>
      ) : null}
      {onCreate ? (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: '1px solid #eef0f4',
          }}
        >
          <Button
            fullWidth
            size="small"
            variant="contained"
            startIcon={<AddCircleOutlineOutlinedIcon fontSize="small" />}
            disabled={isBusy}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onCreate(template);
            }}
            sx={{
              minHeight: 34,
              fontSize: 13,
              fontWeight: 700,
              borderRadius: 1,
            }}
          >
            Create
          </Button>
          {onCopy ? (
            <Button
              fullWidth
              size="small"
              variant="outlined"
              disabled={isBusy}
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                onCopy(template);
              }}
              sx={{
                minHeight: 34,
                mt: 1,
                fontSize: 13,
                fontWeight: 700,
                borderRadius: 1,
              }}
            >
              {isCopying ? 'Copying...' : 'Copy to my templates'}
            </Button>
          ) : null}
        </Box>
      ) : null}
      <Menu
        anchorEl={menuAnchor}
        open={isMenuOpen}
        onClose={() => setMenuAnchor(null)}
        slotProps={{
          paper: {
            sx: {
              borderRadius: '3px',
              boxShadow: '0 1px 4px #cccccc',
              minWidth: 150,
            },
          },
        }}
      >
        {onShare ? (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              setMenuAnchor(null);
              onShare(template);
            }}
            sx={{ fontSize: 14, fontWeight: 700, py: 1, px: 1 }}
          >
            {isSharing ? 'Sharing...' : 'Share template'}
          </MenuItem>
        ) : null}
        {onEdit ? (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              setMenuAnchor(null);
              onEdit(template);
            }}
            sx={{ fontSize: 14, fontWeight: 700, py: 1, px: 1 }}
          >
            {isUpdating ? 'Saving...' : 'Edit template'}
          </MenuItem>
        ) : null}
        {onDelete ? (
          <MenuItem
            disabled={isBusy}
            onClick={() => {
              setMenuAnchor(null);
              onDelete(template);
            }}
            sx={{ fontSize: 14, fontWeight: 700, py: 1, px: 1 }}
          >
            {isDeleting ? 'Deleting...' : 'Delete template'}
          </MenuItem>
        ) : null}
      </Menu>
    </Card>
  );
};
